import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tags, ChefHat, X, Camera } from "lucide-react";
import { toast } from "sonner";
import { brl } from "@/lib/format";

export default function Products() {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [inv, setInv] = useState([]);
  const [dlgP, setDlgP] = useState(false);
  const [dlgC, setDlgC] = useState(false);
  const [dlgR, setDlgR] = useState(false);
  const [editP, setEditP] = useState(null);
  const [recipeProduct, setRecipeProduct] = useState(null);
  const [recipe, setRecipe] = useState([]);
  const [pForm, setPForm] = useState({ name: "", description: "", price: 0, category_id: "", image_url: "", active: true });
  const [uploading, setUploading] = useState(false);
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPForm((f) => ({ ...f, image_url: r.data.url })); toast.success("Foto enviada");
    } catch (err) { if (err.response?.status !== 402) toast.error(err.response?.data?.detail || "Erro ao enviar foto"); }
    setUploading(false); e.target.value = "";
  };
  const [cForm, setCForm] = useState({ name: "", order: 0 });

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/categories"), api.get("/products")]);
    setCats(c.data); setProds(p.data);
    try { const i = await api.get("/inventory"); setInv(i.data); } catch { setInv([]); }
  };
  useEffect(() => { load(); }, []);

  const saveProduct = async () => {
    try {
      const body = { ...pForm, price: parseFloat(pForm.price) };
      if (editP) await api.patch(`/products/${editP.id}`, body); else await api.post("/products", body);
      toast.success("Produto salvo"); setDlgP(false); setEditP(null); setPForm({ name: "", description: "", price: 0, category_id: "", image_url: "", active: true }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const delProduct = async (id) => { await api.delete(`/products/${id}`); toast.success("Removido"); load(); };
  const saveCat = async () => {
    try { await api.post("/categories", cForm); toast.success("Categoria criada"); setDlgC(false); setCForm({ name: "", order: 0 }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const openEdit = (p) => { setEditP(p); setPForm({ name: p.name, description: p.description || "", price: p.price, category_id: p.category_id || "", image_url: p.image_url || "", active: p.active }); setDlgP(true); };

  const openRecipe = async (p) => {
    setRecipeProduct(p);
    try {
      const r = await api.get(`/products/${p.id}/recipe`);
      setRecipe(r.data.items || []);
      setDlgR(true);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Ficha técnica disponível no plano Profissional");
    }
  };
  const addRecipeRow = () => setRecipe([...recipe, { inventory_id: inv[0]?.id || "", quantity: 1 }]);
  const updRecipe = (i, key, val) => setRecipe(recipe.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const rmRecipe = (i) => setRecipe(recipe.filter((_, idx) => idx !== i));
  const saveRecipe = async () => {
    try {
      await api.put(`/products/${recipeProduct.id}/recipe`, { items: recipe.map((r) => ({ ...r, quantity: parseFloat(r.quantity) })) });
      toast.success("Ficha técnica salva"); setDlgR(false);
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-3xl font-extrabold text-stone-900">Cardápio</h1>
          <p className="text-stone-500">Gerencie categorias, produtos e ficha técnica.</p></div>
        <div className="flex gap-2">
          <Dialog open={dlgC} onOpenChange={setDlgC}>
            <DialogTrigger asChild><Button variant="outline" className="gap-2" data-testid="new-category-btn"><Tags className="h-4 w-4" /> Nova categoria</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>Nova categoria</DialogTitle></DialogHeader>
              <div className="space-y-3"><Label>Nome</Label><Input value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} data-testid="category-name" /></div>
              <DialogFooter><Button onClick={saveCat} data-testid="category-save">Salvar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={dlgP} onOpenChange={(o) => { setDlgP(o); if (!o) setEditP(null); }}>
            <DialogTrigger asChild><Button className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="new-product-btn"><Plus className="h-4 w-4" /> Novo produto</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{editP ? "Editar" : "Novo"} produto</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome</Label><Input value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} data-testid="product-name" /></div>
                <div><Label>Descrição</Label><Textarea value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} data-testid="product-desc" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Preço (R$)</Label><Input type="number" step="0.01" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} data-testid="product-price" /></div>
                  <div><Label>Categoria</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={pForm.category_id} onChange={(e) => setPForm({ ...pForm, category_id: e.target.value })} data-testid="product-category">
                      <option value="">Sem categoria</option>
                      {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div><Label>Foto do produto</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {pForm.image_url && <img src={pForm.image_url} alt="" className="h-16 w-16 rounded-lg object-cover border" data-testid="product-image-preview" />}
                    <label className="flex-1 cursor-pointer rounded-md border border-dashed border-stone-300 px-3 py-3 text-sm text-center hover:bg-stone-50" data-testid="product-photo-label">
                      <Camera className="h-4 w-4 inline mr-1" /> {uploading ? "Enviando..." : "Tirar foto ou escolher arquivo"}
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} disabled={uploading} data-testid="product-photo-input" />
                    </label>
                  </div>
                  <Input className="mt-2" placeholder="ou cole a URL de uma imagem" value={pForm.image_url} onChange={(e) => setPForm({ ...pForm, image_url: e.target.value })} data-testid="product-image" />
                </div>
              </div>
              <DialogFooter><Button onClick={saveProduct} className="bg-orange-600 hover:bg-orange-700" data-testid="product-save">Salvar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {cats.length === 0 && <Card><CardContent className="p-6 text-stone-500 text-sm">Crie categorias antes de cadastrar produtos.</CardContent></Card>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {prods.map((p) => (
          <Card key={p.id} className="rounded-2xl overflow-hidden" data-testid={`product-card-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt="" className="w-full h-40 object-cover" />}
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-display font-bold text-stone-900">{p.name}</div>
                  <div className="text-xs text-stone-500">{cats.find((c) => c.id === p.category_id)?.name || "Sem categoria"}</div>
                  {p.recipe?.length > 0 && <div className="text-xs text-emerald-600 mt-1 font-semibold">Ficha técnica ({p.recipe.length})</div>}
                </div>
                <div className="font-mono-num font-bold text-orange-600">{brl(p.price)}</div>
              </div>
              <p className="text-sm text-stone-500 mt-2 line-clamp-2">{p.description}</p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="outline" size="sm" onClick={() => openRecipe(p)} data-testid={`recipe-${p.id}`}><ChefHat className="h-3.5 w-3.5" /></Button>
                <Button variant="outline" size="sm" onClick={() => delProduct(p.id)} data-testid={`del-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dlgR} onOpenChange={setDlgR}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Ficha técnica — {recipeProduct?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-stone-500">Ao confirmar um pedido, os insumos aqui listados serão baixados automaticamente do estoque.</p>
          {inv.length === 0 && <p className="text-sm text-rose-600">Cadastre itens de estoque antes de montar a ficha.</p>}
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {recipe.map((r, i) => (
              <div key={i} className="flex gap-2 items-end" data-testid={`recipe-row-${i}`}>
                <div className="flex-1">
                  <Label className="text-xs">Insumo</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={r.inventory_id} onChange={(e) => updRecipe(i, "inventory_id", e.target.value)}>
                    {inv.map((it) => <option key={it.id} value={it.id}>{it.name} ({it.unit})</option>)}
                  </select>
                </div>
                <div className="w-28">
                  <Label className="text-xs">Qtd</Label>
                  <Input type="number" step="0.001" value={r.quantity} onChange={(e) => updRecipe(i, "quantity", e.target.value)} />
                </div>
                <Button variant="ghost" size="icon" onClick={() => rmRecipe(i)}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addRecipeRow} disabled={inv.length === 0} data-testid="recipe-add">
            <Plus className="h-4 w-4 mr-1" /> Adicionar insumo
          </Button>
          <DialogFooter><Button onClick={saveRecipe} className="bg-orange-600 hover:bg-orange-700" data-testid="recipe-save">Salvar ficha</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
